/**
 * Memory Configuration Tab
 *
 * Displays configurable parameters grouped by category with edit controls.
 */
import { useState } from 'react'
import { Card } from '@personic/shared'
import { useMemoryConfig, useMemoryPresets } from '../../../hooks/useMemory'

export function MemoryConfig() {
  const { data: config, isLoading } = useMemoryConfig()
  const { data: presets } = useMemoryPresets()
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null)

  if (isLoading) {
    return (
      <Card className="p-6 bg-neutral-900/50 border-neutral-800 animate-pulse">
        <div className="h-48" />
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Presets */}
      <Card className="p-6 bg-neutral-900/50 border-neutral-800">
        <h3 className="text-lg font-semibold text-white mb-4">Configuration Presets</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {presets && Object.entries(presets).map(([name, preset]) => (
            <button
              key={name}
              onClick={() => setSelectedPreset(name)}
              className={`p-3 rounded-lg border text-left transition-colors ${
                selectedPreset === name
                  ? 'border-purple-500 bg-purple-500/10'
                  : 'border-neutral-700 bg-neutral-800/50 hover:border-neutral-600'
              }`}
            >
              <p className="text-sm font-medium text-white capitalize">{name.replace(/_/g, ' ')}</p>
              <p className="text-xs text-neutral-400 mt-1">
                {(preset as Record<string, unknown>).context_budget_tokens as number ?? 700} tokens
              </p>
            </button>
          ))}
        </div>
      </Card>

      {/* Current Configuration */}
      <Card className="p-6 bg-neutral-900/50 border-neutral-800">
        <h3 className="text-lg font-semibold text-white mb-4">Current Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {config && Object.entries(config).map(([key, value]) => (
            <div key={key} className="flex justify-between items-center py-2 border-b border-neutral-800">
              <span className="text-sm text-neutral-400">{key.replace(/_/g, ' ')}</span>
              <span className="text-sm text-white font-mono">
                {typeof value === 'boolean' ? (value ? 'Enabled' : 'Disabled') : String(value)}
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}
